'use client';

import { useState, type Dispatch, type SetStateAction, useEffect } from 'react';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { getNmiDetails } from '@/app/queries';
import { TextInput } from '@/components/input';
import { Modal, FormModalContent, ModalButton } from '@/components/modal';
import {
  ANALYTICS_EVENTS,
  BUSINESS_PHONE_NO,
  RESIDENTIAL_SERVICE_PHONE_NO,
} from '@/constants';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

import { Button } from '../button';
import { Text } from '../text';

import { nmiSearchSchema } from './form-schema';

import type { NmiSearchSchema } from './form-schema';

type NmiSearchModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
};

export const NmiSearchModal = ({
  isModalOpen = false,
  setIsModalOpen,
}: NmiSearchModalProps) => {
  const { setPropertyDetails, setPropertyAddressLookupDetails, resetProperty } =
    useJoinFormStore();
  const [isFetching, setIsFetching] = useState<boolean>();

  const formId = 'multiple-address-form';

  function closeModal() {
    setIsModalOpen(false);
  }

  function cancelModal() {
    resetProperty();
    setIsModalOpen(false);
    trackAnalyticsEvent(ANALYTICS_EVENTS.addressNotFoundModal.modalClose, {});
  }

  function openModal() {
    setIsModalOpen(true);
  }

  useEffect(() => {
    if (isModalOpen) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.nmiSearchModal.nmiLookup, {});
    }
  }, [isModalOpen]);

  return (
    <Modal
      heading="Sorry we couldn’t find your address"
      description="Try searching using your National Meter Identifier (NMI). This can also be found on one of your energy bills!"
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      openModal={openModal}
    >
      <FormModalContent<NmiSearchSchema>
        onSubmit={(data, setError) => {
          trackAnalyticsEvent(ANALYTICS_EVENTS.nmiSearchModal.manualSearch);
          trackAnalyticsEvent(
            ANALYTICS_EVENTS.addressNotFoundModal.nmiLookup,
            {}
          );
          setIsFetching(true);
          getNmiDetails(data.nmi)
            .then((value) => {
              // Inject the address directly as refs suck
              const searchInput = document.getElementById(
                'address-lookup-input'
              ) as HTMLInputElement;

              searchInput.value = value.address.full;

              setIsFetching(false);
              setPropertyDetails(value);
              setPropertyAddressLookupDetails(
                value.address.id,
                value.address.full
              );
              closeModal();
            })
            .catch((error) => {
              console.error(error);
              setError('nmi', {
                type: 'server',
                // TODO: confirm message
                message:
                  'Unable to find your NMI details. Please confirm this is correct.',
              });
              setIsFetching(false);
            });
        }}
        schema={nmiSearchSchema}
        id={formId}
        buttonComponent={
          <>
            <ModalButton
              loading={isFetching}
              variant="primary"
              type="submit"
              form={formId}
            >
              Continue
            </ModalButton>

            <ModalButton
              disabled={isFetching}
              variant="tertiary"
              onClick={cancelModal}
            >
              Cancel
            </ModalButton>
          </>
        }
      >
        <TextInput<NmiSearchSchema>
          name="nmi"
          label="National Meter Identifier (NMI)"
          hint="Your NMI should have at least 10 digits"
        />

        <div>
          <Text
            className="mb-a"
            variant="h4"
          >
            Need help?
          </Text>
          <Text variant="body2">
            Sometimes its easier to talk to a person. We’re available between
            8am - 7pm, Monday - Friday (AET)
          </Text>
        </div>
        <div className="flex gap-c">
          <div className="flex flex-1 flex-col gap-a">
            <Text variant="body2">Residential</Text>
            <Button
              icon="phone"
              iconPosition="left"
              variant="secondary"
              href={`tel:${RESIDENTIAL_SERVICE_PHONE_NO}`}
              onClick={() =>
                trackAnalyticsEvent(
                  ANALYTICS_EVENTS.addressNotFoundModal.callResidential,
                  {}
                )
              }
            >
              {RESIDENTIAL_SERVICE_PHONE_NO}
            </Button>
          </div>
          <div className="flex flex-1 flex-col gap-a">
            <Text variant="body2">Business</Text>
            <Button
              icon="phone"
              iconPosition="left"
              href={`tel:${BUSINESS_PHONE_NO}`}
              variant="secondary"
              onClick={() =>
                trackAnalyticsEvent(
                  ANALYTICS_EVENTS.addressNotFoundModal.callBusiness,
                  {}
                )
              }
            >
              1800 728 197
            </Button>
          </div>
        </div>
      </FormModalContent>
    </Modal>
  );
};
